import { useState, type FormEvent } from 'react';
import { api } from '../api';
import { Field, PageHead, Panel, Tabs, toast } from '../components/ui';
import { clampPct, errorMessage, localDate } from '../format';
import { go } from '../router';

type Kind = 'forecast' | 'decision';

export function DecisionNew() {
  const [kind, setKind] = useState<Kind>('forecast');
  const [question, setQuestion] = useState('');
  const [criteria, setCriteria] = useState('');
  const [resolveBy, setResolveBy] = useState(localDate(14));
  const [prob, setProb] = useState(60);
  const [base, setBase] = useState('');
  const [reasoning, setReasoning] = useState('');
  const [premortem, setPremortem] = useState('');
  const [options, setOptions] = useState('');
  const [choice, setChoice] = useState('');
  const [domain, setDomain] = useState('');
  const [saving, setSaving] = useState(false);

  const baseNum = base.trim() === '' || Number.isNaN(Number(base)) ? null : Math.min(100, Math.max(0, Number(base)));
  const warnings: string[] = [];
  if (prob >= 95 || prob <= 5) {
    const ifWrong = (Math.max(prob, 100 - prob) / 100) ** 2;
    warnings.push(`${prob}%는 아주 강한 확신입니다. 틀리면 이 예측 하나의 브라이어가 ${ifWrong.toFixed(2)}입니다 (늘 50%면 0.25).`);
  }
  if (baseNum != null && Math.abs(prob - baseNum) >= 30) {
    warnings.push(`기준율(${baseNum}%)에서 ${Math.abs(prob - baseNum)}%p 떨어져 있습니다. 근거가 그만큼 강한가요?`);
  }

  const save = async (e: FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      const p = await api.createPrediction({
        kind, question, criteria, domain, tags: [], probability: prob / 100,
        base_rate: baseNum == null ? null : baseNum / 100, reasoning, premortem, options, choice, resolve_by: resolveBy,
      });
      toast('기록했습니다 — 마감일에 결과를 입력하세요');
      go(`/decisions/${p.id}`);
    } catch (err) {
      toast(errorMessage(err), 'err');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={save} className="edit-form">
      <PageHead title="새 예측" sub="결과가 '예/아니오'로 분명히 판정되는 질문으로. 1~4주 안에 결과가 나오면 피드백이 빠릅니다." />
      <Panel>
        <Tabs label="기록 종류" value={kind} onChange={setKind} tabs={[{ key: 'forecast', label: '예측' }, { key: 'decision', label: '결정' }]} />
        {kind === 'decision' && (
          <div className="grid-2">
            <Field label="고려한 선택지" htmlFor="p-options" hint="한 줄에 하나씩">
              <textarea id="p-options" className="input" rows={3} value={options} onChange={(e) => setOptions(e.target.value)} />
            </Field>
            <Field label="고른 것" htmlFor="p-choice">
              <input id="p-choice" className="input" value={choice} onChange={(e) => setChoice(e.target.value)} />
            </Field>
          </div>
        )}
        <Field
          label={kind === 'decision' ? '기대하는 결과 (예/아니오 질문)' : '질문 (예/아니오)'}
          htmlFor="p-question"
          hint={kind === 'decision' ? "예: '가격을 2만 원으로 올려도 한 달 안에 10건 이상 팔린다'" : "예: '11월 30일까지 사업자 등록을 마친다'"}
        >
          <input id="p-question" className="input" value={question} onChange={(e) => setQuestion(e.target.value)} required minLength={3} maxLength={500} />
        </Field>
        <div className="grid-2">
          <Field label="판정 기준" htmlFor="p-criteria" hint="무엇을 보면 '예'인가? 남이 봐도 같은 판정이 나오게">
            <input id="p-criteria" className="input" value={criteria} onChange={(e) => setCriteria(e.target.value)} />
          </Field>
          <Field label="마감일" htmlFor="p-due">
            <input id="p-due" className="input" type="date" value={resolveBy} onChange={(e) => setResolveBy(e.target.value)} required />
          </Field>
        </div>
      </Panel>

      <Panel title="확률">
        <div className="prob-input">
          <input type="range" min={1} max={99} value={prob} onChange={(e) => setProb(Number(e.target.value))} aria-label="확률 슬라이더" />
          <label className="prob-number">
            <input className="input" type="number" min={1} max={99} value={prob} onChange={(e) => setProb(clampPct(Number(e.target.value)))} aria-label="확률 (%)" />%
          </label>
        </div>
        <p className="muted small">{prob}% = 이런 상황이 10번 있으면 {(prob / 10).toFixed(1).replace(/\.0$/, '')}번쯤 '예'가 된다고 보는 것</p>
        <Field label="기준율 (외부 관점)" htmlFor="p-base" hint="나를 빼고 비슷한 경우들은 보통 몇 %나 그렇게 됐나? 모르면 비워 두세요">
          <input id="p-base" className="input narrow" type="number" min={0} max={100} value={base} onChange={(e) => setBase(e.target.value)} placeholder="%" />
        </Field>
        {warnings.map((w) => <p key={w} className="warn-box">{w}</p>)}
      </Panel>

      <Panel title="근거와 사전 부검">
        <Field label="판단 근거" htmlFor="p-reason">
          <textarea id="p-reason" className="input" rows={3} value={reasoning} onChange={(e) => setReasoning(e.target.value)} />
        </Field>
        <Field label="사전 부검: 틀린다면 가장 그럴듯한 이유는?" htmlFor="p-pre" hint="미리 적어 두면 결과가 나온 뒤 '원래 알았다'는 착각을 막아 줍니다">
          <textarea id="p-pre" className="input" rows={2} value={premortem} onChange={(e) => setPremortem(e.target.value)} />
        </Field>
        <Field label="분야" htmlFor="p-domain">
          <input id="p-domain" className="input" value={domain} onChange={(e) => setDomain(e.target.value)} placeholder="예: 사업, 투자, 개인, 사회" />
        </Field>
      </Panel>

      <div className="form-actions">
        <button type="button" className="btn" onClick={() => history.back()}>취소</button>
        <button type="submit" className="btn btn-primary" disabled={saving}>기록하기</button>
      </div>
    </form>
  );
}
